import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, ViewChild} from "@angular/core";
import {NavigationService} from "../../orchestration/navigation.service";
import {ValidationMessage} from "@fedelta-pos/data-client";
import {NotificationService} from "../../notification/notification.service";
import {Subscription} from "rxjs";
import {PossessivePipe} from "../../string-utils/possessive.pipe";
import {LoadingService} from "../../ui-elements/loading/loading.service";
import {ButtonConnotation, ButtonProminence} from "../../buttons/button/button";
import {Page} from "../../orchestration/page";
import {IconButtonType} from "../../buttons/icon-button/icon-button";
import {GroupOrderPageMode} from "./group-order-page-mode";
import {MessageLevel} from "../../notification/message/message";
import {SelectorType} from "../../inputs/selector-control/selector";
import {GroupOrderModel} from "../group-order.model";
import {IdentityModel} from "../../identity/identity.model";
import {OrderModel, TransferRequest} from "../../order/order.model";
import {GroupOrderEvents} from "../group-order-events";
import {OrderEvents} from "../../order/order-events";
import {FormComponent} from "../../inputs/form/form.component";
import {ReorderStatus} from "../../ordering-page/reorder-status";
import {NavigationParams} from "../../orchestration/navigation-params";
import {OrderTransferTargetStatus} from "../order-transfer-request-poll.service";
import {OrderTransferTargetReferenceData} from "../order-transfer-target-entity.model";

@Component({
    selector: "woa-group-order-page",
    templateUrl: "./group-order-page.component.html",
    providers: [{provide: Page, useExisting: GroupOrderPageComponent}]
})
export class GroupOrderPageComponent extends Page implements AfterViewInit, OnDestroy {
    ButtonProminence = ButtonProminence;
    ButtonConnotation = ButtonConnotation;
    OrderTransferTargetStatus = OrderTransferTargetStatus;
    IconButtonType = IconButtonType;
    GroupOrderPageMode = GroupOrderPageMode;
    MessageLevel = MessageLevel;
    SelectorType = SelectorType;

    @ViewChild(FormComponent) public form: FormComponent;

    mode: GroupOrderPageMode;
    newGroupOrderName: string;
    currentGroupOrderName: string;
    error: string;
    errorParams: any;
    groupOrderStatus: OrderTransferTargetStatus;
    reorderStatus: ReorderStatus;
    instructionsExpanded: boolean = false;
    orderFirstName: string;
    hideNewGroupOrder: boolean;
    targetsExpanded: boolean = false;
    otherTransferTargets: OrderTransferTargetReferenceData[];
    existingTransferTarget: OrderTransferTargetReferenceData;
    newestOtherTransferTarget: OrderTransferTargetReferenceData;
    selectedTransferTarget: OrderTransferTargetReferenceData;

    requestRemoved: { [transferRequestId: string]: boolean } = {};

    private subscriptions: Subscription = new Subscription();

    constructor(navigationParams: NavigationParams,
                private loadingService: LoadingService,
                private identityModel: IdentityModel,
                private changeDetectorRef: ChangeDetectorRef,
                private notificationService: NotificationService,
                public navigationService: NavigationService,
                public orderModel: OrderModel,
                public groupOrderModel: GroupOrderModel) {
        super();

        this.mode = navigationParams.get("mode");
        this.hideNewGroupOrder = !!navigationParams.get("hideNewGroupOrder");
        this.reorderStatus = navigationParams.get("reorderStatus");

        // Edit mode
        this.groupOrderStatus = GroupOrderEvents.groupOrderStatus$.getValue();
        if (this.reorderStatus) {
            this.newGroupOrderName = this.reorderStatus.reorderGroupOrderName;
        } else if ((this.groupOrderModel.currentOrderTransferTargetEntityModel.data && this.groupOrderModel.currentOrderTransferTargetEntityModel.data.id) || this.groupOrderStatus === OrderTransferTargetStatus.CLOSED_TIMEOUT) {
            this.newGroupOrderName = this.groupOrderModel.currentOrderTransferTargetEntityModel.data.name;
        }
        this.currentGroupOrderName = this.newGroupOrderName;
        if (!this.newGroupOrderName && this.identityModel.currentIdentity) {
            this.newGroupOrderName = this.identityModel.currentIdentity.firstName;
        }
        this.orderFirstName = this.identityModel.currentIdentity.firstName;

        // Participant mode
        if (this.groupOrderModel.selectedTransferTarget) {
            this.existingTransferTarget = this.orderModel.initiatedOrder.availableTransferTargets
                .find((target: OrderTransferTargetReferenceData) => target.id === this.groupOrderModel.selectedTransferTarget.id);
        }
        this.otherTransferTargets = orderModel.initiatedOrder.availableTransferTargets
            .filter((target: OrderTransferTargetReferenceData) => target !== this.existingTransferTarget);
        if (this.otherTransferTargets.length) {
            this.newestOtherTransferTarget = this.otherTransferTargets[0];
            this.groupOrderModel.lastTransferTargetProposed = this.newestOtherTransferTarget.created.getTime();
            this.selectedTransferTarget = this.newestOtherTransferTarget;
        } else {
            this.selectedTransferTarget = this.existingTransferTarget;
        }
    }

    ngAfterViewInit() {
        this.subscriptions.add(OrderEvents.orderChanged$.subscribe(() => this.changeDetectorRef.detectChanges()));
        if (this.reorderStatus) {
            if (this.reorderStatus.groupOrderValidationMessages) {
                this.form.applyServerValidationMessages(this.reorderStatus.groupOrderValidationMessages);
            } else if (this.reorderStatus.groupOrderError) {
                if (this.reorderStatus.groupOrderError.displayable) {
                    this.error = this.reorderStatus.groupOrderError.data.messageKey;
                    this.errorParams = this.reorderStatus.groupOrderError.data.messageParams;
                } else {
                    this.error = "woa.message.group-order.error.recreateFailed";
                    this.errorParams = undefined;
                }
            }
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    async onWillDismissPage(confirmed: boolean): Promise<boolean> {
        if (this.reorderStatus && !confirmed) {
            await this.closeGroupOrder();
        }
        return true;
    }

    get title(): string {
        return this.mode === GroupOrderPageMode.PARTICIPANTS ? (this.newestOtherTransferTarget ? "woa.heading.group-order.title.join" : "woa.heading.group-order.title.joined")
            : this.groupOrderStatus === OrderTransferTargetStatus.OPEN ? "woa.heading.group-order.title.update"
                : !this.groupOrderModel.canStartGroupOrder ? "woa.heading.group-order.title.rescan"
                    : this.reorderStatus ? "woa.heading.group-order.title.recreate"
                        : this.groupOrderStatus === OrderTransferTargetStatus.CLOSED ? "woa.heading.group-order.title.create"
                            : "woa.heading.group-order.title.continue";
    }

    get showEditIntroHeading(): boolean {
        return this.groupOrderModel.canStartGroupOrder && this.groupOrderStatus === OrderTransferTargetStatus.CLOSED;
    }

    get showEditInstructions(): boolean {
        return this.groupOrderStatus === OrderTransferTargetStatus.CLOSED && this.orderModel.totalTransfers === 0;
    }

    get editIntroBody(): string {
        return !this.groupOrderModel.canStartGroupOrder ? "woa.message.group-order.intro.rescan"
            : this.groupOrderStatus === OrderTransferTargetStatus.OPEN ? "woa.message.group-order.intro.update"
                : this.groupOrderStatus === OrderTransferTargetStatus.CLOSED ? "woa.message.group-order.intro.create.body"
                    : this.groupOrderStatus === OrderTransferTargetStatus.CLOSED_TIMEOUT ? (this.orderModel.totalTransfers ? "woa.message.group-order.intro.continueTimeout.withRequests" : "woa.message.group-order.intro.continueTimeout.withoutRequests")
                        : this.groupOrderStatus === OrderTransferTargetStatus.CLOSED_CONFLICT ? (this.orderModel.totalTransfers ? "woa.message.group-order.intro.continueConflict.withRequests" : "woa.message.group-order.intro.continueConflict.withoutRequests")
                            : undefined;
    }

    get participantIntroHeading(): string {
        return this.newestOtherTransferTarget ? "woa.message.group-order.intro.join.heading" : "woa.message.group-order.intro.joined.heading";
    }

    get participantIntroBody(): string {
        return this.newestOtherTransferTarget ? "woa.message.group-order.intro.join.body" : "woa.message.group-order.intro.joined.body";
    }

    get showEditParticipants(): boolean {
        return this.groupOrderStatus === OrderTransferTargetStatus.OPEN || this.orderModel.totalTransfers > 0;
    }

    get editParticipantsHeading(): string {
        return this.groupOrderStatus === OrderTransferTargetStatus.CLOSED ? "woa.heading.group-order.participants.closed" : "woa.heading.group-order.participants.open";
    }

    toggleParticipantTargets(targetList: HTMLElement): void {
        this.targetsExpanded = !this.targetsExpanded;
        setTimeout(() => {
            targetList.style.maxHeight = this.targetsExpanded ? (targetList.scrollHeight + 2) + "px" : "0";
            if (this.targetsExpanded) {
                setTimeout(() => targetList.scrollIntoView({
                    block: "end",
                    inline: "nearest",
                    behavior: "smooth"
                }), 330);
            }
        });
    }

    selectParticipantTarget(target: OrderTransferTargetReferenceData): void {
        this.selectedTransferTarget = target;
    }

    get saveButtonLabel(): string {
        return !this.groupOrderModel.canStartGroupOrder ? "woa.button.common.ok"
            : this.groupOrderStatus === OrderTransferTargetStatus.OPEN ? "woa.button.group-order.update"
                : this.reorderStatus ? "woa.button.group-order.recreate"
                    : this.groupOrderStatus === OrderTransferTargetStatus.CLOSED ? "woa.button.group-order.create"
                        : "woa.button.group-order.continue";
    }

    get cancelButtonLabel(): string {
        return this.reorderStatus ? "woa.button.group-order.noRecreate" : "woa.button.group-order.cancel";
    }

    get joinButtonLabel(): string {
        return this.newestOtherTransferTarget ? "woa.button.group-order.join" : "woa.button.group-order.keep";
    }

    toggleRequest(request: TransferRequest): void {
        if (this.requestRemoved[request.transferRequestId]) {
            delete this.requestRemoved[request.transferRequestId];
        } else {
            this.requestRemoved[request.transferRequestId] = true;
        }
    }

    toggleEditInstructions(instructions: HTMLElement): void {
        this.instructionsExpanded = !this.instructionsExpanded;
        setTimeout(() => {
            instructions.style.maxHeight = this.instructionsExpanded ? (instructions.scrollHeight + 1) + "px" : "0";
            if (this.instructionsExpanded) {
                setTimeout(() => instructions.scrollIntoView({
                    block: "end",
                    inline: "nearest",
                    behavior: "smooth"
                }), 330);
            }
        });
    }

    async saveGroupOrder(): Promise<any> {
        if (!this.groupOrderModel.canStartGroupOrder) {
            return this.navigationService.dismissPage(this);
        }

        this.error = undefined;
        if (!this.form.valid) {
            return;
        }

        await this.loadingService.performWithSpinner(async () => {
            let success: boolean = false;
            try {
                const validationMessages: ValidationMessage[] = await this.groupOrderModel.saveGroupOrder(this.groupOrderStatus !== OrderTransferTargetStatus.OPEN, this.newGroupOrderName);
                await this.loadingService.dismissSpinner();
                if (validationMessages) {
                    if (this.groupOrderStatus === OrderTransferTargetStatus.OPEN) {
                        this.groupOrderModel.currentOrderTransferTargetEntityModel.data.name = this.currentGroupOrderName;
                    }
                    this.form.applyServerValidationMessages(validationMessages);
                } else {
                    success = true;
                }
            } catch (error) {
                if (this.groupOrderStatus === OrderTransferTargetStatus.OPEN) {
                    this.groupOrderModel.currentOrderTransferTargetEntityModel.data.name = this.currentGroupOrderName;
                }
                if (error.displayable) {
                    this.error = error.data.messageKey;
                    this.errorParams = error.data.messageParams;
                } else {
                    this.error = this.groupOrderStatus === OrderTransferTargetStatus.OPEN ? "woa.message.group-order.error.updateFailed" : "woa.message.group-order.error.createFailed";
                    this.errorParams = undefined;
                }
            }

            if (success) {
                if (Object.keys(this.requestRemoved).length) {
                    this.orderModel.removeTransferRequestOrderItems((transferRequestId: string) => this.requestRemoved[transferRequestId]);
                }

                await this.navigationService.dismissPage(this, true);
            }
        }, undefined, this.groupOrderStatus === OrderTransferTargetStatus.OPEN ? "woa.message.group-order.updating" : "woa.message.group-order.creating");
    }

    async closeGroupOrder(): Promise<any> {
        if (this.groupOrderStatus !== OrderTransferTargetStatus.OPEN && !this.reorderStatus) {
            return this.navigationService.dismissPage(this);
        }

        if (await this.notificationService.displayConfirmation(this.reorderStatus ? "woa.heading.group-order.confirmNoRecreate" : "woa.heading.group-order.confirmCancel", undefined,
            this.reorderStatus ? "woa.message.group-order.confirmNoRecreate" : "woa.message.group-order.confirmCancelGroupOrder",
            "woa.button.common.noThanks",
            this.reorderStatus ? "woa.button.group-order.discard" : "woa.button.group-order.cancel",
            true,
            {groupOrderName: new PossessivePipe().transform(this.currentGroupOrderName)})) {
            await this.groupOrderModel.closeGroupOrder(OrderTransferTargetStatus.CLOSED, !this.reorderStatus, true);
            return this.navigationService.dismissPage(this, true);
        }
    }

    joinGroupOrder(transferTarget: OrderTransferTargetReferenceData | Event): void {
        if (!this.form.valid) {
            return;
        }

        this.identityModel.currentIdentity.firstName = this.orderFirstName;
        this.groupOrderModel.selectedTransferTarget = transferTarget instanceof Event ? this.selectedTransferTarget : transferTarget;

        // noinspection JSIgnoredPromiseFromCall
        this.navigationService.dismissPage(this, true);
    }

    startNewGroupOrder(): void {
        this.groupOrderModel.selectedTransferTarget = undefined;
        this.mode = GroupOrderPageMode.EDIT;
    }
}



// WEBPACK FOOTER //
// ./src/app/group-order/group-order-page/group-order-page.component.ts